import {useEffect, useRef} from 'react'

const useProductTileHover = ({
    isOOS,
    isNotifyMeModalOpen,
    isShopSimilarModalOpen,
    setIsHovered,
    setIsQuickViewOpen,
    setMobileQuickViewOpenInternal,
    triggerFreshLikesFetch,
    tileRef
}) => {
    const lastViewportWidthRef = useRef(
        typeof window !== 'undefined' ? window.innerWidth : 0
    )

    const handleMouseEnter = () => {
        if (!isOOS) {
            setIsHovered(true)
        }
        // Trigger fresh likes count fetch on hover to bypass cache (BD specification)
        triggerFreshLikesFetch()
    }

    const handleMouseLeave = () => {
        if (!isNotifyMeModalOpen && !isOOS) {
            setIsHovered(false)
        }
    }

    useEffect(() => {
        if (!isNotifyMeModalOpen) {
            setIsHovered(false)
        }
    }, [isNotifyMeModalOpen, setIsHovered])

    useEffect(() => {
        const handleInteractOutside = (event) => {
            if (isNotifyMeModalOpen || isShopSimilarModalOpen) {
                return
            }

            const target = event?.target
            if (!(target instanceof Node)) {
                return
            }

            // Robust containment check. We consult the event's composed path in
            // addition to `contains()` because Safari can report a misleading
            // `event.target` and the tile re-renders after a variation is
            // selected. composedPath reflects the DOM the event actually
            // traversed at dispatch time.
            const currentTile = tileRef?.current
            const path = typeof event.composedPath === 'function' ? event.composedPath() : []
            const isInsideCurrentTile = Boolean(
                currentTile && (currentTile.contains(target) || path.includes(currentTile))
            )
            if (isInsideCurrentTile) {
                return
            }

            const quickViewTrigger = target.closest?.('[data-testid="quick-add"]')
            if (quickViewTrigger || !target.closest?.('[data-product-tile="true"]')) {
                setIsHovered(false)
                setIsQuickViewOpen(false)
            }
        }

        // Pointer-based dismissal only. We intentionally do NOT listen for
        // `focusin`: macOS Safari does not focus a <button> on click and instead
        // moves focus to <body>, which fired a false "outside" dismissal that
        // closed the Quick View before the add-to-cart click could run. Focus is
        // already trapped within the open Quick View (FocusTrap), so focus-based
        // dismissal is redundant.
        document.addEventListener('pointerdown', handleInteractOutside, true)

        return () => {
            document.removeEventListener('pointerdown', handleInteractOutside, true)
        }
    }, [isNotifyMeModalOpen, isShopSimilarModalOpen, setIsHovered, setIsQuickViewOpen])

    useEffect(() => {
        const hideQuickViewMobile = () => {
            // Android virtual keyboard can trigger resize with same width
            // (height-only change). Do not close quick view in that case.
            const nextWidth = window.innerWidth
            const hasViewportWidthChanged = nextWidth !== lastViewportWidthRef.current
            lastViewportWidthRef.current = nextWidth

            if (!hasViewportWidthChanged) {
                return
            }

            setMobileQuickViewOpenInternal(false)
        }

        window.addEventListener('resize', hideQuickViewMobile)
        return () => window.removeEventListener('resize', hideQuickViewMobile)
    }, [setMobileQuickViewOpenInternal])

    return {handleMouseEnter, handleMouseLeave}
}

export default useProductTileHover
